<script setup>
/**
 * Add or edit an MCP connector: what to call, how to reach it, and what to
 * present when it asks who you are.
 *
 * The credential is the reason this dialog is shaped the way it is. It is
 * write-only — the API answers with `auth_configured` and never the secret —
 * so an edit cannot round-trip it. Leaving the credential alone therefore has
 * to mean omitting `auth_config` from the payload entirely, which is exactly
 * what the model reads as "keep what you have". Replacing one is an explicit
 * act, behind its own control, so nobody clears a working credential by
 * opening the form and pressing Save.
 *
 * Testing runs against the saved row by id whenever there is one. That is the
 * only path on which the stored credential is the thing being probed; a probe
 * built from the form alone would be testing an anonymous request.
 */
import { computed, ref } from 'vue';
import { useI18n } from 'vue-i18n';

import Button from 'dashboard/components-next/button/Button.vue';
import Dialog from 'dashboard/components-next/dialog/Dialog.vue';
import Input from 'dashboard/components-next/input/Input.vue';
import Select from 'dashboard/components-next/select/Select.vue';
import Switch from 'dashboard/components-next/switch/Switch.vue';

defineProps({
  isSubmitting: { type: Boolean, default: false },
  isTesting: { type: Boolean, default: false },
  // `{ ok, message, serverInfo, protocolVersion, tools }` from the last probe.
  testResult: { type: Object, default: null },
});

const emit = defineEmits(['submit', 'test', 'close']);

const { t } = useI18n();

// Both values are the one Streamable HTTP transport; they record only what
// response shape the operator expects back.
const TRANSPORTS = ['http', 'sse'];
// `none` is not a stored shape - it is the absence of a credential.
const AUTH_TYPES = ['none', 'bearer', 'header'];

const dialogRef = ref(null);
const editing = ref(null);
const name = ref('');
const endpointUrl = ref('');
const transport = ref('http');
const enabled = ref(true);
const authType = ref('none');
const token = ref('');
const headerName = ref('');
const headerValue = ref('');
const isReplacingCredential = ref(false);

const isEditing = computed(() => Boolean(editing.value));
const hasStoredCredential = computed(() =>
  Boolean(editing.value?.auth_configured)
);

// A connector with a credential on file hides the fields until the admin says
// they mean to replace it. Everything else shows them straight away.
const showCredentialFields = computed(
  () => !hasStoredCredential.value || isReplacingCredential.value
);

const transportOptions = computed(() =>
  TRANSPORTS.map(value => ({
    value,
    label: t(`MCP_CONNECTORS.FORM.TRANSPORT_${value.toUpperCase()}`),
  }))
);

const authTypeOptions = computed(() =>
  AUTH_TYPES.map(value => ({
    value,
    label: t(`MCP_CONNECTORS.FORM.AUTH_${value.toUpperCase()}`),
  }))
);

// Built only when the fields are on screen and filled: an omitted `auth_config`
// is what preserves the stored one.
const authConfig = computed(() => {
  if (!showCredentialFields.value) return null;
  if (authType.value === 'bearer' && token.value.trim()) {
    return { type: 'bearer', token: token.value.trim() };
  }
  if (
    authType.value === 'header' &&
    headerName.value.trim() &&
    headerValue.value.trim()
  ) {
    return {
      type: 'header',
      name: headerName.value.trim(),
      value: headerValue.value.trim(),
    };
  }
  return null;
});

// A half-filled credential is rejected rather than silently dropped - dropping
// it would save a connector that reaches the remote server anonymously.
const hasIncompleteCredential = computed(
  () =>
    showCredentialFields.value && authType.value !== 'none' && !authConfig.value
);

const canSubmit = computed(
  () =>
    Boolean(name.value.trim()) &&
    Boolean(endpointUrl.value.trim()) &&
    !hasIncompleteCredential.value
);

const buildPayload = () => {
  const payload = {
    name: name.value.trim(),
    endpoint_url: endpointUrl.value.trim(),
    transport: transport.value,
    enabled: enabled.value,
  };
  if (authConfig.value) payload.auth_config = authConfig.value;
  return payload;
};

const open = (connector = null) => {
  editing.value = connector;
  name.value = connector?.name || '';
  endpointUrl.value = connector?.endpoint_url || '';
  transport.value = connector?.transport || 'http';
  enabled.value = connector ? Boolean(connector.enabled) : true;
  authType.value = 'none';
  token.value = '';
  headerName.value = '';
  headerValue.value = '';
  isReplacingCredential.value = false;
  dialogRef.value?.open();
};

const close = () => dialogRef.value?.close();

const onSubmit = () => {
  if (!canSubmit.value) return;
  emit('submit', { id: editing.value?.id || null, payload: buildPayload() });
};

// A saved connector is probed by id so the stored credential is the one under
// test; anything typed in the form is overlaid on the row server-side.
const onTest = () => {
  if (!canSubmit.value) return;
  emit('test', { id: editing.value?.id || null, payload: buildPayload() });
};

defineExpose({ open, close });
</script>

<template>
  <Dialog
    ref="dialogRef"
    width="2xl"
    overflow-y-auto
    :title="
      isEditing
        ? t('MCP_CONNECTORS.FORM.EDIT_TITLE')
        : t('MCP_CONNECTORS.FORM.CREATE_TITLE')
    "
    :confirm-button-label="
      isEditing
        ? t('MCP_CONNECTORS.FORM.SAVE')
        : t('MCP_CONNECTORS.FORM.CREATE')
    "
    :cancel-button-label="t('MCP_CONNECTORS.FORM.CANCEL')"
    :disable-confirm-button="!canSubmit"
    :is-loading="isSubmitting"
    :close-on-click-outside="false"
    @confirm="onSubmit"
    @close="emit('close')"
  >
    <div class="flex flex-col gap-5" data-testid="mcp-connector-dialog">
      <Input
        v-model="name"
        :label="t('MCP_CONNECTORS.FORM.NAME_LABEL')"
        :placeholder="t('MCP_CONNECTORS.FORM.NAME_PLACEHOLDER')"
        data-testid="mcp-connector-name"
      />

      <Input
        v-model="endpointUrl"
        :label="t('MCP_CONNECTORS.FORM.ENDPOINT_LABEL')"
        :placeholder="t('MCP_CONNECTORS.FORM.ENDPOINT_PLACEHOLDER')"
        :message="t('MCP_CONNECTORS.FORM.ENDPOINT_HINT')"
        data-testid="mcp-connector-endpoint"
      />

      <div class="flex flex-col gap-1">
        <label class="text-sm font-medium text-n-slate-12">
          {{ t('MCP_CONNECTORS.FORM.TRANSPORT_LABEL') }}
        </label>
        <Select
          v-model="transport"
          :options="transportOptions"
          data-testid="mcp-connector-transport"
        />
        <p class="mb-0 text-xs text-n-slate-11">
          {{ t('MCP_CONNECTORS.FORM.TRANSPORT_HINT') }}
        </p>
      </div>

      <fieldset class="flex flex-col gap-3 p-0 m-0 border-0">
        <legend class="p-0 mb-1 text-sm font-medium text-n-slate-12">
          {{ t('MCP_CONNECTORS.FORM.AUTH_TITLE') }}
        </legend>

        <div
          v-if="hasStoredCredential && !isReplacingCredential"
          class="flex items-center justify-between gap-3 p-3 border rounded-lg border-n-weak"
          data-testid="mcp-connector-stored-credential"
        >
          <span class="flex flex-col gap-0.5">
            <span class="text-sm text-n-slate-12">
              {{ t('MCP_CONNECTORS.FORM.CREDENTIAL_ON_FILE') }}
            </span>
            <span class="text-xs text-n-slate-11">
              {{ t('MCP_CONNECTORS.FORM.CREDENTIAL_ON_FILE_HINT') }}
            </span>
          </span>
          <Button
            variant="ghost"
            color="slate"
            size="sm"
            :label="t('MCP_CONNECTORS.FORM.REPLACE_CREDENTIAL')"
            data-testid="mcp-connector-replace-credential"
            @click="isReplacingCredential = true"
          />
        </div>

        <template v-if="showCredentialFields">
          <Select
            v-model="authType"
            :options="authTypeOptions"
            data-testid="mcp-connector-auth-type"
          />

          <Input
            v-if="authType === 'bearer'"
            v-model="token"
            type="password"
            :label="t('MCP_CONNECTORS.FORM.TOKEN_LABEL')"
            :placeholder="t('MCP_CONNECTORS.FORM.TOKEN_PLACEHOLDER')"
            data-testid="mcp-connector-token"
          />

          <template v-if="authType === 'header'">
            <Input
              v-model="headerName"
              :label="t('MCP_CONNECTORS.FORM.HEADER_NAME_LABEL')"
              :placeholder="t('MCP_CONNECTORS.FORM.HEADER_NAME_PLACEHOLDER')"
              data-testid="mcp-connector-header-name"
            />
            <Input
              v-model="headerValue"
              type="password"
              :label="t('MCP_CONNECTORS.FORM.HEADER_VALUE_LABEL')"
              data-testid="mcp-connector-header-value"
            />
          </template>
        </template>
      </fieldset>

      <div class="flex items-start gap-3 p-3 border rounded-lg border-n-weak">
        <Switch v-model="enabled" data-testid="mcp-connector-enabled" />
        <span class="flex flex-col gap-0.5">
          <span class="text-sm text-n-slate-12">
            {{ t('MCP_CONNECTORS.FORM.ENABLED_TITLE') }}
          </span>
          <span class="text-xs text-n-slate-11">
            {{ t('MCP_CONNECTORS.FORM.ENABLED_DESCRIPTION') }}
          </span>
        </span>
      </div>

      <div class="flex flex-col gap-2">
        <div class="flex">
          <Button
            variant="outline"
            color="slate"
            size="sm"
            icon="i-lucide-plug-zap"
            :label="t('MCP_CONNECTORS.FORM.TEST')"
            :disabled="!canSubmit || isTesting"
            :is-loading="isTesting"
            data-testid="mcp-connector-test"
            @click="onTest"
          />
        </div>

        <div
          v-if="testResult"
          class="flex flex-col gap-2 p-3 text-sm border rounded-lg border-n-weak"
          data-testid="mcp-connector-test-result"
        >
          <span
            :class="testResult.ok ? 'text-n-teal-11' : 'text-n-ruby-11'"
            class="font-medium"
          >
            {{ testResult.message }}
          </span>
          <span v-if="testResult.serverInfo" class="text-xs text-n-slate-11">
            {{
              t('MCP_CONNECTORS.TEST.SERVER', {
                name: testResult.serverInfo.name,
                version: testResult.serverInfo.version,
              })
            }}
          </span>
          <ul
            v-if="testResult.tools && testResult.tools.length"
            class="flex flex-col gap-1 m-0 list-none"
          >
            <li
              v-for="tool in testResult.tools"
              :key="tool.name"
              class="text-xs text-n-slate-11"
            >
              <span class="font-medium text-n-slate-12">{{ tool.name }}</span>
              <span v-if="tool.description"> — {{ tool.description }}</span>
            </li>
          </ul>
        </div>
      </div>
    </div>
  </Dialog>
</template>
